import React, {useMemo, useState} from "react";
import {BrowserRouter, Route, Switch, Redirect} from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { useWeb3React } from "@web3-react/core";

import 'react-toastify/dist/ReactToastify.css';

import {ReactComponent as VotingIcon} from "./assets/voting.svg";
import {ReactComponent as StakingIcon} from "./assets/staking.svg";
import {ReactComponent as FAQIcon} from "./assets/faq.svg";
import {ReactComponent as BackIcon} from "./assets/back.svg";

import Navigation from "./components/Navigation/Navigation";
import Staking from "./components/Staking/Staking";
import {useEthereum} from "./EthereumContext";
import Voting from "./components/Voting/Voting";
import ProposalVoting from "./components/Voting/ProposalVoting";
import {useDispatch} from "react-redux";
// import {fetchGovTokenData} from "./redux/govToken";
import NewProposal from "./components/Proposal/NewProposal/NewProposal";
import {fetchProposalDetails, fetchProposals} from "./redux/governor";
import { useFetchStaking } from './redux/hooks/useFetchStaking';
import { useFetchGovTokenData } from './redux/hooks/useFetchGovTokenData';
import { AuthorizeModalProvider } from './components/AuthorizeModal';
import { useAutoConnect } from './hooks';
import { OnlyBSCModal } from './shared/components';
import './App.css'
import useResize from "./hooks/useResize";
import Header from "./components/Header";
import Footer from "./components/Footer";

function App() {
    const { chainId } = useWeb3React();
    const {account, staking, roobee, xRoobee, validNetwork, governor} = useEthereum();
    const dispatch = useDispatch();
    useAutoConnect();
    const { windowWidth } = useResize();
    const mobileView = useMemo(() => windowWidth <= 600, [windowWidth]);

    const [openMenu, setOpenMenu] = useState(false)

    const activeMenu = () => {
        setOpenMenu((prevCheck) => !prevCheck)
    }

    const { fetchStaking } = useFetchStaking();
    const { fetchGovToken } = useFetchGovTokenData();

    React.useEffect(() => {
        if (validNetwork) {
            fetchStaking();
        }
    }, [account, staking, roobee, validNetwork, fetchStaking]);

    React.useEffect(() => {
        if (staking && validNetwork) {
            staking.on("Staked", fetchStaking);
            staking.on("Unlocked", fetchStaking);
            staking.on("Withdrawn", fetchStaking);
            staking.on("RewardPaid", fetchStaking);
        }
    }, [staking, validNetwork, fetchStaking]);

    React.useEffect(() => {
        if (validNetwork) {
            fetchGovToken();
        }
    }, [account, xRoobee, validNetwork, fetchGovToken]);

    React.useEffect(() => {
        if (xRoobee && validNetwork) {
            xRoobee.on("Mint", fetchGovToken);
            xRoobee.on("Burn", fetchGovToken);
            xRoobee.on("DelegateChanged", fetchGovToken);
            xRoobee.on("DelegateVotesChanged", fetchGovToken);
        }
    }, [xRoobee, validNetwork, fetchGovToken]);

    React.useEffect(() => {
        if (governor && validNetwork) {
            governor.on("ProposalCreated", () => dispatch(fetchProposals(governor)));
            governor.on("VoteCast", (voter, proposalId) => dispatch(fetchProposalDetails({governor, account}, proposalId)));
        }
    }, [governor, validNetwork, dispatch, account]);

    const routes = [
        {url: "/", exact: true, component: <Redirect to="/voting" />},
        {url: "/voting", exact: true, name: "Voting", component: <Voting />, icon: <VotingIcon />},
        {url: "/staking", name: "Staking", component: <Staking />, icon: <StakingIcon />},
        {url: "/voting/:id", component: <ProposalVoting />},
        {url: "/proposal", component: <NewProposal />},
        {url: "https://roobee.gitbook.io/roobee-eng/products/roobee-dao", external: true, blank: true, name: "FAQ", icon: <FAQIcon />},
        {url: "https://roobee.finance/", external: true, name: "Go to Roobee Finance", icon: <BackIcon />}
    ];

    return (
        <AuthorizeModalProvider>
            <BrowserRouter>
                <ToastContainer />
                <Switch>
                    {routes.filter(route => route.component).map(route => (
                        <Route path={route.url} exact={route.exact} key={route.url}>
                            <div className="container">
                                <Navigation active={openMenu} routes={routes} activeUrl={route.url} />
                                {mobileView ? <Header onClick={activeMenu} active={openMenu} /> : null}
                                {
                                    !!chainId && chainId !== 56 ? (
                                    <OnlyBSCModal />
                                ) : (
                                    <div className="main-content">
                                        {route.component}
                                    </div>
                                    )
                                }
                                {mobileView ? <Footer /> : null}
                            </div>
                        </Route>
                    ))}
                    <Route path="/">
                        <Redirect to={{pathname: routes[0].url}} />
                    </Route>
                </Switch>
            </BrowserRouter>
        </AuthorizeModalProvider>
    );
}

export default App;
